import { getInitDatePromise } from "@/constants/initTimeHelper";
import { LayerKindName } from "@/layers/utility/createLayerObject";
import { roundToNearestFiveMinutes } from "@/time/roundToNearstFiveMinutes";
import type { JSONInitDate } from "@mm/api.meteomatics.com";
import type { CartographicMapOutput } from "@mm/metx-workbench.meteomatics.com";
import { type DateTime, Duration } from "luxon";
import { useEffect, useState } from "react";

type MapLayer = NonNullable<CartographicMapOutput["layers"]>[number];
type InitTimeLayer = MapLayer & {
  model: string;
  parameter_unit: string;
  show_init_time: boolean;
  custom_options?: { init_date?: string };
};

function isInitTimeLayer(layer: MapLayer): layer is InitTimeLayer {
  return "model" in layer && "parameter_unit" in layer && "show_init_time" in layer && layer.show_init_time === true;
}

/**
 * Resolve the init-date rows shown in the map label.
 *
 * This is shared by the normal DOM label and the lightweight recording canvas label
 * so both render the same init-date information without duplicating lookup logic.
 *
 * For each visible init-time-enabled layer, the hook either:
 * - uses an explicit custom init date from layer options, or
 * - looks up the init date for the current display time plus map time offset
 */
export function useMapInitDates(
  layers: CartographicMapOutput["layers"],
  displayTime: DateTime | null | undefined,
  timeOffsetMinutes: number,
) {
  const [allInitDates, setAllInitDates] = useState<JSONInitDate[]>([]);

  useEffect(() => {
    if (!(layers && displayTime)) {
      setAllInitDates([]);
      return;
    }

    const initDatePromises = layers
      .filter(isInitTimeLayer)
      .map((layer) => {
        const customInitDate =
          typeof layer.custom_options?.init_date === "string" ? layer.custom_options.init_date.trim() : "";
        if (customInitDate) {
          const initDate: JSONInitDate = {
            parameter: layer.parameter_unit,
            model: layer.model,
            initDate: customInitDate,
          };
          return Promise.resolve(initDate);
        }

        let tmpDisplayTime = displayTime;
        if (layer.kind !== LayerKindName.LightningLayerDescription) {
          tmpDisplayTime = roundToNearestFiveMinutes(displayTime);
        }

        const validDateWithOffset = tmpDisplayTime.plus(Duration.fromObject({ minutes: timeOffsetMinutes }));
        return getInitDatePromise(layer, validDateWithOffset.toJSDate());
      })
      .filter((obj): obj is Promise<JSONInitDate> => obj !== null);

    if (initDatePromises.length === 0) {
      setAllInitDates([]);
      return;
    }

    Promise.all(initDatePromises)
      .then((initDates) => {
        const uniqueInitDates = Array.from(new Set(initDates));
        setAllInitDates(uniqueInitDates);
      })
      .catch((err) => console.error(err));
  }, [layers, displayTime, timeOffsetMinutes]);

  return allInitDates;
}
